import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGalleryTreeApiSessionsGalleryTreeGetQueryKey,
  listSessionsApiSessionsGetQueryKey,
} from "../generated/api/@tanstack/react-query.gen";
import { renameSessionApiSessionsSessionIdPatch } from "../generated/api/sdk.gen";
import type { GalleryTreeResponse } from "../generated/api/types.gen";

/** Rename a design session, optimistically updating the gallery tree cache
 *  and then revalidating both session queries. Shared by the gallery tree
 *  rows and the refine top bar. */
export function useRenameSession() {
  const queryClient = useQueryClient();
  const treeQueryKey = getGalleryTreeApiSessionsGalleryTreeGetQueryKey();

  const mutation = useMutation({
    mutationFn: async (p: { sessionId: string; name: string }) => {
      await renameSessionApiSessionsSessionIdPatch({
        path: { session_id: p.sessionId },
        body: { name: p.name },
      });
    },
    onSuccess: (_data, { sessionId, name }) => {
      queryClient.setQueryData(
        treeQueryKey,
        (current: GalleryTreeResponse | undefined) => {
          if (!current?.sessions) return current;
          return {
            ...current,
            sessions: current.sessions.map((session) =>
              session.session_id === sessionId ? { ...session, name } : session,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: treeQueryKey });
      queryClient.invalidateQueries({
        queryKey: listSessionsApiSessionsGetQueryKey(),
      });
    },
  });

  return (sessionId: string, name: string): Promise<void> =>
    mutation.mutateAsync({ sessionId, name });
}
